import { createSlice } from '@reduxjs/toolkit';
import { TAX_PROFILE_STATUS, TaxProfileState } from '../types/payout.types';
import {
    fetchTaxProfile,
    fetchConsentUrl,
    fetchInterview,
    updateTaxProfile,
    fetchAccountStatus,
    fetchRetakeAvailable,
    fetchTaxDocuments,
    checkEmailExisted
} from '../actions/tax.actions';

export const TAX_PROFILE_DEFAULT_STATE: TaxProfileState = {
    loading: false,
    loadingUrl: false,
    interviewFailed: false,
    accountStatusLoading: false,
    retakeAvailable: false,
    fetchedTaxProfile: false
};

const taxProfileSlice = createSlice({
    name: 'taxProfile.info',
    initialState: TAX_PROFILE_DEFAULT_STATE,
    reducers: {
        resetInterviewFailed: (state) => {
            state.interviewFailed = false;
        },
        resetInterviewCurrentUrl: (state) => {
            state.taxInterviewUrl = undefined;
        },
        setSelectedTaxYear: (state, action) => {
            state.selectedTaxYear = action.payload;
        },
        resetFetchTaxProfile: (state) => {
            state.fetchedTaxProfile = false;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTaxProfile.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchTaxProfile.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.taxProfile = action.payload;
                state.taxProfileStatus = action.payload?.taxStatus;
                if (!action?.payload?.recordIdentityKey) {
                    state.taxProfileStatus = TAX_PROFILE_STATUS.NULL;
                }
                state.fetchedTaxProfile = true;
            })
            .addCase(fetchTaxProfile.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(fetchConsentUrl.pending, (state) => {
                state.loadingUrl = true;
                state.error = null;
            })
            .addCase(fetchConsentUrl.fulfilled, (state, action) => {
                state.loadingUrl = false;
                state.taxInterviewUrl = action.payload.taxInterviewURL;
            })
            .addCase(fetchConsentUrl.rejected, (state, action) => {
                state.loadingUrl = false;
                state.error = action.payload as string;
                state.interviewFailed = true;
            })
            .addCase(fetchInterview.pending, (state) => {
                state.loadingUrl = true;
                state.error = null;
            })
            .addCase(fetchInterview.fulfilled, (state, action) => {
                state.loadingUrl = false;
                state.taxInterviewUrl = action.payload.taxInterviewURL;
            })
            .addCase(fetchInterview.rejected, (state, action) => {
                state.loadingUrl = false;
                state.error = action.payload as string;
                state.interviewFailed = true;
            })
            .addCase(updateTaxProfile.pending, (state) => {
                state.updating = true;
                state.error = null;
            })
            .addCase(updateTaxProfile.fulfilled, (state, action) => {
                state.updating = false;
                state.error = null;
                state.taxProfile = action.payload;
                state.taxProfileStatus = action.payload?.taxStatus;
                if (!action.payload.recordIdentityKey) {
                    state.taxProfileStatus = TAX_PROFILE_STATUS.NULL;
                }
            })
            .addCase(updateTaxProfile.rejected, (state, action) => {
                state.updating = false;
                state.error = action.payload as string;
            })
            .addCase(fetchAccountStatus.pending, (state) => {
                state.accountStatusLoading = true;
                state.error = null;
            })
            .addCase(fetchAccountStatus.fulfilled, (state, action) => {
                state.accountStatusLoading = false;
                state.error = null;
                state.accountStatus = action.payload.data.accountStatus;
            })
            .addCase(fetchAccountStatus.rejected, (state, _action) => {
                state.accountStatusLoading = false;
            })
            .addCase(fetchRetakeAvailable.fulfilled, (state, action) => {
                state.retakeAvailable = action.payload.retakeAvailable;
            })
            .addCase(fetchTaxDocuments.pending, (state) => {
                state.taxDocumentsLoading = true;
                state.error = null;
            })
            .addCase(fetchTaxDocuments.fulfilled, (state, action) => {
                state.taxDocumentsLoading = false;
                state.error = null;
                state.taxDocuments = action.payload.data.documents;
                state.yearLength = action.payload.data.yearLength;
            })
            .addCase(fetchTaxDocuments.rejected, (state, action) => {
                state.taxDocumentsLoading = false;
                state.taxDocumentsError = action.payload as string;
            })
            .addCase(checkEmailExisted.pending, (state) => {
                state.checkingEmail = true;
                state.emailExisted = null;
            })
            .addCase(checkEmailExisted.fulfilled, (state, action) => {
                state.checkingEmail = false;
                state.emailExisted = action.payload.emailExisted;
            })
            .addCase(checkEmailExisted.rejected, (state) => {
                state.checkingEmail = false;
                state.emailExisted = null;
            });
    }
});

export const { resetInterviewFailed, resetInterviewCurrentUrl, setSelectedTaxYear } =
    taxProfileSlice.actions;
export const taxProfileReducer = taxProfileSlice.reducer;
